import "./App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation, useNavigationType } from "react-router-dom";
import HomePage from "./pages/HomePage";
import CollectionPage from "./pages/CollectionPage";
import ProductDetailPage from "./pages/ProductDetailPage";
import StaticPage from "./pages/StaticPage";
import ConnectionPage from "./pages/ConnectionPage";
import AccountLogin from "./pages/account/Login";
import AccountIndex from "./pages/account/Index";
import AccountLogout from "./pages/account/Logout";
import AccountComplete from "./pages/account/Complete";
import BuildMyLoadout from "./pages/BuildMyLoadout";
import AdminCommunity from "./pages/AdminCommunity";
import AdminHeroDrop from "./pages/AdminHeroDrop";
import AdminPreorderBatches from "./pages/AdminPreorderBatches";
import AdminDrops from "./pages/AdminDrops";
import DropsPage from "./pages/DropsPage";
import InStockPage from "./pages/InStockPage";
import WriteReviewPage from "./pages/WriteReviewPage";
import AdminReviews from "./pages/AdminReviews";
import AdminRestockAlerts from "./pages/AdminRestockAlerts";
import AboutPage from "./pages/AboutPage";
import ContactPage from "./pages/ContactPage";
import ShippingReturnsPage from "./pages/ShippingReturnsPage";
import PreOrderPolicyPage from "./pages/PreOrderPolicyPage";
import FaqPage from "./pages/FaqPage";
import TrackOrderPage from "./pages/TrackOrderPage";
import WarrantyPage from "./pages/WarrantyPage";
import PrivacyPage from "./pages/PrivacyPage";
import TermsPage from "./pages/TermsPage";
import CookiesPage from "./pages/CookiesPage";
import OperatorLabIndex from "./pages/operator-lab/OperatorLabIndex";
import GripIndex from "./pages/operator-lab/GripIndex";
import FifteenKBuild from "./pages/operator-lab/FifteenKBuild";
import HallEffectVsMechanical from "./pages/operator-lab/HallEffectVsMechanical";
import AimTheory101 from "./pages/operator-lab/AimTheory101";
import LegacyRoute from "./pages/LegacyRoute";
import { AccountProvider } from "./state/account";
import { CartProvider } from "./state/cart";
import CartDrawer from "./components/cart/CartDrawer";
import { CATEGORIES } from "./lib/catalog/categories";

// Preserve inbound links to /products/:handle (older marketing surfaces,
// shared URLs, sitemap entries) by 302-equivalent client redirect to the
// new /p/:handle canonical path (spec STEP 6).
// EXCEPTION: draft/retired handles (data/draft-product-redirects.json)
// never reach /p/<slug> — drafts are invisible to the Storefront API and
// would render "product not found". They bounce to their configured
// collection instead, mirroring the edge 301 in setupProxy.js.
import draftProductRedirects from "./data/draft-product-redirects.json";

function LegacyProductRedirect() {
  const slug = window.location.pathname.replace(/^\/products\//, "").replace(/\/+$/, "");
  const search = window.location.search || "";
  const configured = draftProductRedirects[slug];
  return <Navigate to={(configured || `/p/${slug}`) + search} replace />;
}

// PROMPT 3.0 §1: /p/:handle serves EITHER the category PLP (when handle ∈
// category IDs) OR the individual PDP. The four category IDs are reserved
// keywords in this namespace.
const CATEGORY_IDS = new Set(CATEGORIES.map((c) => c.id));

function ProductOrCategoryRoute() {
  const { handle } = useParams();
  if (CATEGORY_IDS.has(handle)) {
    return <CollectionPage />;
  }
  return <ProductDetailPage />;
}

// Reset scroll on forward navigation to a new page; Back/Forward (POP)
// keeps the browser's restored position. Query-only changes (filters) don't jump.
function ScrollToTop() {
  const { pathname, hash } = useLocation();
  const navType = useNavigationType();
  useEffect(() => {
    if (navType === "POP") return undefined;
    if (!hash) { window.scrollTo(0, 0); return undefined; }
    // Anchor links (e.g. /contact#business-legal): wait for the page to render.
    const t = setTimeout(() => document.getElementById(hash.slice(1))?.scrollIntoView({ block: "start" }), 120);
    return () => clearTimeout(t);
  }, [pathname, hash, navType]);
  return null;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <AccountProvider>
          <CartProvider>
            <CartDrawer />
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/collections/:handle" element={<CollectionPage />} />
              {/* Legacy /products/:handle → new /p/:handle (spec STEP 6). */}
              <Route path="/products/:handle" element={<LegacyProductRedirect />} />
              <Route path="/p/:handle" element={<ProductOrCategoryRoute />} />
              <Route path="/pages/:slug" element={<StaticPage />} />
              <Route path="/cart" element={<StaticPage />} />
              <Route path="/connection" element={<ConnectionPage />} />
              <Route path="/account/login" element={<AccountLogin />} />
              <Route path="/account/orders" element={<AccountIndex />} />
              <Route path="/account/index" element={<AccountIndex />} />
              <Route path="/account/complete" element={<AccountComplete />} />
              <Route path="/account/logout" element={<AccountLogout />} />
              <Route path="/build-my-loadout" element={<BuildMyLoadout />} />
              <Route path="/admin/community" element={<AdminCommunity />} />
              <Route path="/admin/hero-drop" element={<AdminHeroDrop />} />
              <Route path="/admin/preorder-batches" element={<AdminPreorderBatches />} />
              <Route path="/admin/drops" element={<AdminDrops />} />
              <Route path="/drops" element={<DropsPage />} />
              <Route path="/in-stock" element={<InStockPage />} />
              <Route path="/write-review" element={<WriteReviewPage />} />
              <Route path="/admin/reviews" element={<AdminReviews />} />
              <Route path="/admin/restock-alerts" element={<AdminRestockAlerts />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/shipping-returns" element={<ShippingReturnsPage />} />
              <Route path="/preorder-policy" element={<PreOrderPolicyPage />} />
              <Route path="/faq" element={<FaqPage />} />
              <Route path="/track-order" element={<TrackOrderPage />} />
              <Route path="/warranty" element={<WarrantyPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/cookies" element={<CookiesPage />} />
              <Route path="/operator-lab" element={<OperatorLabIndex />} />
              <Route path="/operator-lab/aim-theory-101" element={<AimTheory101 />} />
              <Route path="/operator-lab/the-grip-index" element={<GripIndex />} />
              <Route path="/operator-lab/15000-loadout-build" element={<FifteenKBuild />} />
              <Route path="/operator-lab/hall-effect-vs-mechanical" element={<HallEffectVsMechanical />} />
              <Route path="*" element={<LegacyRoute />} />
            </Routes>
          </CartProvider>
        </AccountProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
